"use client"

import { forwardRef, type ReactElement } from "react"

// Story 8.1 (como `BonusField`) → Story 10.6 (extraído para aqui). O CAMPO DE FORMULÁRIO DIRIGIDO PELA
// DEFINIÇÃO: recebe um descritor de campo servido pelo Engine e desenha-se a partir dele. Renderiza os
// 5 membros da união de tipos de forma EXAUSTIVA — um renderer parcial rebentaria com uma configuração
// perfeitamente válida.
//
// PORQUE MUDOU DE SÍTIO: nasceu em `modules/bonus/components/bonus-field` com o comentário "é a 2.ª
// ocorrência do padrão: duplica-se agora e extrai-se depois" (a 1.ª é o `CheckoutField`). A candidatura
// a afiliado (10.6) seria a 3.ª — logo a regra de 3 da casa disparou e o componente veio para um sítio
// NEUTRO. Não é uma abstração especulativa: são dois consumidores reais hoje (`/bonus`,
// `/novos-afiliados`) e já era genérico sobre o descritor.
//
// PORQUE NÃO O `CheckoutField`: diverge do contrato de UX (44px e fundo sólido, contra 48px e vidro),
// não tem `textarea` nem `select`, e não é `forwardRef` — sem ref é IMPOSSÍVEL ancorar o foco no 1.º
// campo inválido. Corrigi-lo arrastaria o checkout da 3.3 para re-verificação.
//
// Contrato de UX cumprido aqui: fundo de vidro, altura 48px (`h-12`), label ACIMA, anel de foco
// visível, erro com borda `danger` + mensagem anunciada e ligada por `aria-describedby`.

export type FormFieldElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

/**
 * O DESCRITOR de um campo, na forma MÍNIMA que este renderer precisa.
 *
 * Declarado aqui e não importado de um modelo concreto (`bonus-form-model`,
 * `application-form-model`), de propósito: é o componente partilhado que fixa o que precisa, e cada
 * modelo continua dono do SEU contrato com o SEU endpoint. Os dois casam por tipagem ESTRUTURAL — se um
 * modelo deixar de satisfazer esta forma, o `tsc` falha no call-site, que é onde a incompatibilidade
 * de facto existe.
 */
export type FormFieldDescriptor = {
  name: string
  label: string
  type: "text" | "email" | "tel" | "select" | "textarea"
  required: boolean
  maxLength: number
  options?: readonly string[]
  helpText?: string
  autoComplete?: string
}

type FormFieldProps = {
  field: FormFieldDescriptor
  value: string
  onChange: (value: string) => void
  onBlur: () => void
  error?: string | null
  /**
   * Prefixo do `data-testid` do controlo (`${testIdPrefix}-${field.name}`).
   *
   * É um prop e não um literal fixo porque os testids são a superfície que o QA e os e2e endereçam:
   * fundir os dois formulários num só prefixo renomearia os do Bónus (`bonus-field-nome`), que já
   * existem, por causa de uma extração que não devia mudar comportamento nenhum.
   */
  testIdPrefix: string
}

// Vidro: não existe token `glass` no tema — é composto à mão (mesma receita do nav,
// `modules/layout/templates/nav/index.tsx:30`).
const GLASS_SURFACE = "border bg-white/[0.03] backdrop-blur-xl"
const CONTROL_BASE = `w-full rounded-rounded px-3 text-text placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-accent ${GLASS_SURFACE}`
// 48px — o contrato de UX é explícito, e é também o alvo de toque confortável em mobile.
const CONTROL_HEIGHT = "h-12"
// A `textarea` é o único controlo que NÃO tem 48px: é multi-linha por definição. Mantém o resto do
// contrato (vidro, foco, erro) e ganha altura para o texto ser legível enquanto se escreve.
const TEXTAREA_HEIGHT = "min-h-32 py-3"

const FormField = forwardRef<FormFieldElement, FormFieldProps>(function FormField(
  { field, value, onChange, onBlur, error = null, testIdPrefix },
  ref,
): ReactElement {
  const errorId = `${field.name}-error`
  const helpId = `${field.name}-help`
  const borderClass = error ? "border-danger" : "border-white/10"

  // `aria-describedby` liga o campo ao erro E ao texto de apoio (quando existem): é o que faz o leitor
  // de ecrã anunciar o motivo da recusa junto do campo, em vez de o deixar num vermelho mudo.
  const describedBy = [error ? errorId : null, field.helpText ? helpId : null]
    .filter((id): id is string => id !== null)
    .join(" ")

  const shared = {
    id: field.name,
    name: field.name,
    value,
    onBlur,
    // Vem da DEFINIÇÃO, como tudo o resto — e fica no `shared` (não só no `<input>`) porque o browser
    // também autopreenche `textarea` e `select`. OMITIR não é neutro: sem atributo o browser aplica as
    // suas heurísticas; `"off"` é o que as desliga. Quem decide é a definição servida pelo Engine.
    autoComplete: field.autoComplete,
    // O `*` ao lado do rótulo é PINTURA: é um `<span>` fora do nome acessível, logo quem usa leitor de
    // ecrã não o ouve. Sem isto, a obrigatoriedade só se descobre depois de submeter e falhar.
    "aria-required": field.required ? true : undefined,
    "aria-invalid": error ? true : undefined,
    "aria-describedby": describedBy.length > 0 ? describedBy : undefined,
    "data-testid": `${testIdPrefix}-${field.name}`,
  }

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={field.name} className="text-sm font-medium text-subtle">
        {field.label}
        {field.required && <span className="ml-0.5 text-danger-bright">*</span>}
      </label>

      {field.type === "textarea" ? (
        <textarea
          {...shared}
          ref={ref as React.Ref<HTMLTextAreaElement>}
          // `maxLength` vem da definição, nunca de um número no JSX. É só a 1.ª barreira (UX): o
          // Engine revalida — colar por script ignora este atributo.
          maxLength={field.maxLength}
          onChange={(event) => onChange(event.target.value)}
          className={`${CONTROL_BASE} ${TEXTAREA_HEIGHT} ${borderClass}`}
        />
      ) : field.type === "select" ? (
        <select
          {...shared}
          ref={ref as React.Ref<HTMLSelectElement>}
          onChange={(event) => onChange(event.target.value)}
          // As `option` nativas herdam o fundo do SO (branco): sem isto, texto claro em fundo claro.
          className={`${CONTROL_BASE} ${CONTROL_HEIGHT} ${borderClass} [&>option]:bg-surface [&>option]:text-text`}
        >
          <option value="">Selecione...</option>
          {field.options?.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : (
        <input
          {...shared}
          ref={ref as React.Ref<HTMLInputElement>}
          type={field.type}
          maxLength={field.maxLength}
          // `tel` não reformata o valor (ver a nota da máscara em `modules/bonus/lib/bonus-form-model`):
          // o que ajuda quem preenche é o teclado numérico e o autofill, não mexer no que ele escreveu.
          inputMode={field.type === "tel" ? "tel" : undefined}
          onChange={(event) => onChange(event.target.value)}
          className={`${CONTROL_BASE} ${CONTROL_HEIGHT} ${borderClass}`}
        />
      )}

      {field.helpText && (
        <span id={helpId} className="text-xs text-muted">
          {field.helpText}
        </span>
      )}

      {error && (
        <p id={errorId} role="alert" className="text-sm text-danger-bright">
          {error}
        </p>
      )}
    </div>
  )
})

export default FormField
